import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { trackEvent } from "@/lib/api";

const LINKS = [
  { label: "Home", href: "#home", testid: "nav-home-link" },
  { label: "About", href: "#about", testid: "nav-about-link" },
  { label: "Apps", href: "#apps", testid: "nav-apps-link", ev: "apps_section" },
  { label: "Contact", href: "#contact", testid: "nav-contact-link" },
];

export default function Header({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href, ev, label) => {
    if (ev) trackEvent(ev, { button_name: label || href, destination_url: href });
    setOpen(false);
    onNavigate(href);
  };

  const ink = scrolled ? "text-charcoal" : "text-ivory";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-xl border-b border-charcoal/10"
            : "bg-charcoal/0 border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <button onClick={() => go("#home")} data-testid="nav-logo" className="flex items-center">
            <img src="/logo.png" alt="Kannur TheyyamNow" className="h-12 md:h-14 w-auto" />
          </button>

          <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={l.testid}
                onClick={() => go(l.href, l.ev, l.ev ? `${l.label} Menu` : undefined)}
                className={`text-xs uppercase tracking-[0.22em] font-medium transition-colors duration-500 hover:text-vermillion ${
                  scrolled ? "text-charcoal/80" : "text-ivory/85"
                }`}
              >
                {l.label}
              </button>
            ))}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => go("#apps", "download_app_click", "Download App")}
              data-testid="nav-download-cta"
              className="bg-vermillion text-ivory text-xs uppercase tracking-[0.18em] font-medium px-6 py-3 transition-colors duration-300 hover:bg-blood"
            >
              Download App
            </motion.button>
          </nav>

          <button
            className={`md:hidden transition-colors duration-500 ${ink}`}
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[110] bg-charcoal flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-[72px]">
              <img src="/logo.png" alt="Kannur TheyyamNow" className="h-11 w-auto" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="text-ivory">
                <X size={28} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <div key={l.href} className="overflow-hidden">
                  <motion.button
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    onClick={() => go(l.href, l.ev, l.ev ? `${l.label} Menu` : undefined)}
                    data-testid={`mobile-${l.testid}`}
                    className="font-serif text-5xl text-ivory py-3 flex items-baseline gap-4 hover:text-gold transition-colors duration-300"
                  >
                    <span className="text-sm text-gold font-sans tracking-[0.2em]">0{i + 1}</span>
                    {l.label}
                  </motion.button>
                </div>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                onClick={() => go("#apps", "download_app_click", "Download App")}
                data-testid="mobile-nav-download-cta"
                className="mt-8 self-start bg-vermillion text-ivory text-xs uppercase tracking-[0.2em] font-medium px-8 py-4"
              >
                Download App
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
